import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, gbp, num, dt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, StageBadge } from "@/components/tam/ui";
import { FollowUps } from "@/components/tam/FollowUps";

function Kpi({ label, value, hint, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 tam-fade" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-3xl font-bold text-[#0B132B] mt-2">{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-slate-500" data-testid="dashboard-loading">Loading dashboard…</p>;
  const pct = d.total_accounts ? Math.round((d.contacted / d.total_accounts) * 100) : 0;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle="Lost & non-trading account recovery overview" />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Kpi label="Target accounts" value={num(d.total_accounts)} testId="kpi-total-accounts" />
        <Kpi label="Historic value" value={gbp(d.total_value)} hint={`${num(d.total_qty)} qty · 2022 – 2026 YTD`} testId="kpi-total-value" />
        <Kpi label="Contacted" value={`${pct}%`} hint={`${d.contacted} accounts`} testId="kpi-contacted" />
        <Kpi label="Qualified / Quote" value={num(d.qualified)} hint={`${d.won} won`} testId="kpi-qualified" />
        <Kpi label="Open Purfleet tasks" value={num(d.open_tasks)} testId="kpi-open-tasks" />
      </div>
      <FollowUps />
      <div className="grid lg:grid-cols-12 gap-6 mb-6">
        <Panel title="Lost revenue & qty by year" className="lg:col-span-7" testId="chart-by-year">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <BarChart data={d.by_year}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `£${Math.round(v / 1000)}k`} tick={{ fontSize: 12 }} domain={[0, "auto"]} allowDataOverflow />
                <YAxis yAxisId="q" orientation="right" tick={{ fontSize: 11 }} domain={[0, "auto"]} allowDataOverflow />
                <Tooltip formatter={(v, n) => (n === "Qty" ? num(v) : gbp(v))} />
                <Bar dataKey="value" name="Value" fill="#0B132B" radius={[3, 3, 0, 0]} />
                <Bar dataKey="qty" name="Qty" fill="#D9232A" radius={[3, 3, 0, 0]} yAxisId="q" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Pipeline" className="lg:col-span-5" testId="pipeline-panel">
          <div className="space-y-2.5">
            {d.pipeline.map((p) => (
              <Link key={p.stage} to={`/accounts?stage=${encodeURIComponent(p.stage)}`} className="flex items-center gap-3 group" data-testid={`pipeline-${p.stage.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="w-32 shrink-0"><StageBadge stage={p.stage} /></span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#D9232A] group-hover:bg-[#0B132B] transition-colors" style={{ width: `${d.total_accounts ? (p.count / d.total_accounts) * 100 : 0}%` }} />
                </div>
                <span className="w-12 text-right text-sm font-mono">{p.count}</span>
              </Link>
            ))}
          </div>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-12 gap-6">
        <Panel title="By account manager" className="lg:col-span-5" testId="by-owner-panel">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="pb-2">Sheet</th><th className="pb-2 text-right">Accts</th><th className="pb-2 text-right">Value</th><th className="pb-2 text-right">Contacted</th></tr></thead>
            <tbody>
              {d.by_owner.map((o) => (
                <tr key={o.owner} className="border-t border-slate-100">
                  <td className="py-2"><Link className="hover:text-[#D9232A]" to={`/accounts?owner=${encodeURIComponent(o.owner)}`}>{o.owner}</Link></td>
                  <td className="py-2 text-right font-mono">{o.accounts}</td>
                  <td className="py-2 text-right font-mono">{gbp(o.value)}</td>
                  <td className="py-2 text-right font-mono">{o.contacted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel title="Highest value – not yet contacted" className="lg:col-span-4" testId="top-uncontacted-panel">
          <ul className="divide-y divide-slate-100">
            {d.top_uncontacted.map((a) => (
              <li key={a.id}>
                <Link to={`/accounts/${a.id}`} className="flex justify-between gap-3 py-2 text-sm hover:text-[#D9232A]" data-testid={`top-account-${a.acc_no}`}>
                  <span className="truncate">{a.name}</span><span className="font-mono shrink-0">{gbp(a.total_value)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Recent activity" className="lg:col-span-3" testId="recent-activity-panel">
          {d.recent_activity.length === 0 && <p className="text-sm text-slate-500">No activity logged yet.</p>}
          <ul className="space-y-3">
            {d.recent_activity.map((a) => (
              <li key={a.id} className="text-sm">
                <Link to={`/accounts/${a.account_id}`} className="font-medium hover:text-[#D9232A]">{a.account_name}</Link>
                <div className="flex items-center gap-2 mt-1"><StageBadge stage={a.stage} /><span className="text-xs text-slate-500">{a.channel}</span></div>
                <p className="text-xs text-slate-500 mt-1">{a.user_name} · {dt(a.created_at)}</p>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
